import type { AutomationParameter } from '@/types/api';

/**
 * The documentation: one Markdown page per playbook and template, a troubleshooting guide, and the
 * catalogue they describe.
 *
 * ## Where it comes from
 *
 * `backend/resources/docs`, imported at build time through the `@docs` alias (see vite.config.ts).
 * The backend owns it for two reasons: `bin/console docs:catalog` exports every playbook's and
 * template's options into `catalog.json` there, with a test that fails when the two disagree, and
 * the assistant reads the same pages to answer questions. One copy, three readers.
 *
 * ## Why the options are not in the Markdown
 *
 * The options table on each page is drawn from `catalog.json`, the same data the run form is drawn
 * from. Written by hand, a page would list a choice the playbook no longer offers the first time
 * somebody changed one; drawn from the export, it cannot.
 */

export type DocKind = 'playbook' | 'template' | 'troubleshooting';

export interface DocPage {
  kind: DocKind;
  /** The playbook or template key, or `troubleshooting`. */
  slug: string;
  title: string;
  summary: string;
  keywords: string[];
  /** The page's Markdown, front matter removed. */
  body: string;
}

export interface CatalogPlaybook {
  key: string;
  name: string;
  summary: string;
  description: string;
  details: string[];
  category: string;
  version: string;
  supported_systems: string;
  retries: number;
  timeout_seconds: number;
  parameters: AutomationParameter[];
}

export interface CatalogTemplate {
  key: string;
  name: string;
  summary: string;
  description: string;
  details: string[];
  version: string;
  supported_systems: string;
  changes: { version: string; notes: string }[];
  parameters: AutomationParameter[];
  steps: { playbook: string; playbook_name: string; label: string; optional: boolean }[];
}

interface DocsCatalog {
  playbooks: CatalogPlaybook[];
  templates: CatalogTemplate[];
}

const RAW = import.meta.glob<string>('@docs/**/*.md', { eager: true, query: '?raw', import: 'default' });
const CATALOG_FILES = import.meta.glob<DocsCatalog>('@docs/catalog.json', { eager: true, import: 'default' });

const CATALOG: DocsCatalog = Object.values(CATALOG_FILES)[0] ?? { playbooks: [], templates: [] };

/**
 * Splits `---`-fenced `key: value` front matter from the body. Deliberately small: the pages are
 * ours, and a YAML parser would be a dependency to read five fields.
 */
export function parseFrontMatter(raw: string): { meta: Record<string, string>; body: string } {
  const text = raw.replace(/\r\n/g, '\n');
  const match = /^---\n([\s\S]*?)\n---\n?/.exec(text);

  if (match === null) {
    return { meta: {}, body: text };
  }

  const meta: Record<string, string> = {};

  for (const line of (match[1] ?? '').split('\n')) {
    const colon = line.indexOf(':');

    if (colon > 0) {
      meta[line.slice(0, colon).trim()] = line.slice(colon + 1).trim();
    }
  }

  return { meta, body: text.slice(match[0].length) };
}

function pageFrom(path: string, raw: string): DocPage | null {
  const reference = /\/(playbooks|templates)\/([a-z0-9-]+)\.md$/.exec(path);
  const kind: DocKind | null = reference !== null
    ? reference[1] === 'playbooks' ? 'playbook' : 'template'
    : /\/troubleshooting\.md$/.test(path) ? 'troubleshooting' : null;

  if (kind === null) {
    return null;
  }

  const { meta, body } = parseFrontMatter(raw);

  return {
    kind,
    slug: reference?.[2] ?? 'troubleshooting',
    title: meta.title ?? '',
    summary: meta.summary ?? '',
    keywords: (meta.keywords ?? '').split(',').map((word) => word.trim()).filter((word) => word !== ''),
    body,
  };
}

const PAGES: DocPage[] = Object.entries(RAW)
  .map(([path, raw]) => pageFrom(path, raw))
  .filter((page): page is DocPage => page !== null);

/** Playbook pages, in the catalogue's own order: the order the Playbooks screen lists them in. */
export const PLAYBOOK_DOCS: DocPage[] = CATALOG.playbooks
  .map((playbook) => PAGES.find((page) => page.kind === 'playbook' && page.slug === playbook.key))
  .filter((page): page is DocPage => page !== undefined);

export const TEMPLATE_DOCS: DocPage[] = CATALOG.templates
  .map((template) => PAGES.find((page) => page.kind === 'template' && page.slug === template.key))
  .filter((page): page is DocPage => page !== undefined);

export const TROUBLESHOOTING: DocPage | undefined = PAGES.find((page) => page.kind === 'troubleshooting');

export function findDoc(kind: DocKind, slug: string): DocPage | undefined {
  return PAGES.find((page) => page.kind === kind && page.slug === slug);
}

export function catalogPlaybook(key: string): CatalogPlaybook | undefined {
  return CATALOG.playbooks.find((playbook) => playbook.key === key);
}

export function catalogTemplate(key: string): CatalogTemplate | undefined {
  return CATALOG.templates.find((template) => template.key === key);
}

/**
 * A page's words, lower-cased, for the search box. Title and keywords count more than the body,
 * because somebody searching "sudo" wants User Management first, not every page that mentions it.
 */
export function searchScore(page: DocPage, query: string): number {
  const terms = query.toLowerCase().split(/\s+/).filter((term) => term.length > 1);

  if (terms.length === 0) {
    return 0;
  }

  const title = page.title.toLowerCase();
  const keywords = page.keywords.join(' ').toLowerCase();
  const summary = page.summary.toLowerCase();
  const body = page.body.toLowerCase();
  let score = 0;

  for (const term of terms) {
    const inTitle = title.includes(term);
    const inKeywords = keywords.includes(term);
    const inSummary = summary.includes(term);
    const inBody = body.includes(term);

    if (!inTitle && !inKeywords && !inSummary && !inBody) {
      // Every word has to be somewhere on the page.
      return 0;
    }

    score += (inTitle ? 8 : 0) + (inKeywords ? 5 : 0) + (inSummary ? 3 : 0) + (inBody ? 1 : 0);
  }

  return score;
}

/**
 * The `<title>` for a reference page: the playbook's name, and what the page is.
 *
 * " · Techie Gain" follows it, and a result title much past 65 characters is cut off, so each page
 * gets the longest form that fits: Advanced Security's own name already fills the space.
 */
export function docSeoTitle(page: DocPage): string {
  if (page.kind === 'troubleshooting') {
    return page.title;
  }

  const kind = page.kind === 'playbook' ? 'Playbook' : 'Template';
  const forms = [`${page.title} ${kind}: Options and Fixes`, `${page.title} ${kind} Guide`, `${page.title} ${kind}`];

  return forms.find((form) => form.length <= 51) ?? page.title;
}

/**
 * The meta description for a reference page: its summary, then what the page covers.
 *
 * Summaries are written for the catalogue cards, at 50 to 100 characters, which is short for a
 * search result. The longest ending that keeps the whole within 160 characters is added.
 */
export function docSeoDescription(page: DocPage): string {
  const trimmed = page.summary.trim();
  const summary = /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  const endings =
    page.kind === 'playbook'
      ? [
          'Every option, what it changes on the server, and how to fix the errors it reports.',
          'Its options, what it changes, and how to fix common errors.',
          'Options, changes and fixes.',
        ]
      : page.kind === 'template'
        ? [
            'What it installs, the playbooks it runs, and what to do after the run.',
            'What it installs and what to do after the run.',
            'What it installs.',
          ]
        : [];
  const ending = endings.find((text) => summary.length + 1 + text.length <= 160);

  return ending === undefined ? summary : `${summary} ${ending}`;
}
